/**
 * Modal para crear/editar Unidad (Patrulla)
 */

import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Button, Input, Select } from "@/components/ui";
import { X, Loader2 } from "lucide-react";
import { useCreateUnit, useUpdateUnit } from "@/hooks/useOrganization";
import { useAcademicPeriods } from "@/hooks/useCatalogs";
import type { Unit } from "@/types";

const unitSchema = z.object({
  name: z.string().min(2, "El nombre debe tener al menos 2 caracteres"),
  periodId: z.string().min(1, "Selecciona el periodo"),
  motto: z.string().optional(),
  color: z.string().optional(),
});

type UnitForm = z.infer<typeof unitSchema>;

interface UnitModalProps {
  isOpen: boolean;
  onClose: () => void;
  unit?: Unit | null;
  clubId: string;
}

export default function UnitModal({
  isOpen,
  onClose,
  unit,
  clubId,
}: UnitModalProps) {
  const isEditing = !!unit;

  const createMutation = useCreateUnit();
  const updateMutation = useUpdateUnit();
  const { data: periods } = useAcademicPeriods();

  // Obtener periodo activo como default
  const activePeriod = periods?.find((p) => p.status === "active");

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<UnitForm>({
    resolver: zodResolver(unitSchema),
    defaultValues: {
      name: unit?.name || "",
      periodId: unit?.periodId || activePeriod?.id || "",
      motto: unit?.motto || "",
      color: unit?.color || "#3B82F6",
    },
  });

  const onSubmit = async (data: UnitForm) => {
    try {
      if (isEditing) {
        await updateMutation.mutateAsync({
          id: unit.id,
          data,
          clubId,
        });
      } else {
        await createMutation.mutateAsync({
          ...data,
          clubId,
          status: "active",
          memberCount: 0,
        } as any);
      }
      onClose();
    } catch (error) {
      // Error is handled by the mutation
    }
  };

  const isLoading = createMutation.isPending || updateMutation.isPending;

  if (!isOpen) return null;

  // Colores predefinidos para unidades
  const presetColors = [
    "#EF4444", // Rojo
    "#F97316", // Naranja
    "#F59E0B", // Ámbar
    "#EAB308", // Amarillo
    "#84CC16", // Lima
    "#22C55E", // Verde
    "#14B8A6", // Teal
    "#06B6D4", // Cyan
    "#3B82F6", // Azul
    "#6366F1", // Indigo
    "#8B5CF6", // Violeta
    "#A855F7", // Púrpura
    "#EC4899", // Rosa
    "#6B7280", // Gris
  ];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm">
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-md p-6 max-h-[90vh] overflow-y-auto animate-in fade-in zoom-in-95 duration-200">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold">
            {isEditing ? "Editar Unidad" : "Nueva Unidad"}
          </h2>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-xl transition-colors duration-200"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          <Input
            label="Nombre de la Unidad *"
            {...register("name")}
            error={errors.name?.message}
            placeholder="Ej: Halcones, Águilas, etc."
          />

          <Select
            label="Periodo *"
            {...register("periodId")}
            error={errors.periodId?.message}
          >
            <option value="">Seleccionar periodo</option>
            {periods?.map((period) => (
              <option key={period.id} value={period.id}>
                {period.name} {period.status === "active" && "(Actual)"}
              </option>
            ))}
          </Select>

          <Input
            label="Lema"
            {...register("motto")}
            error={errors.motto?.message}
            placeholder="Ej: Siempre listos"
          />

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Color de la Unidad
            </label>
            <div className="flex flex-wrap gap-2 mb-2">
              {presetColors.map((color) => (
                <label key={color} className="cursor-pointer">
                  <input
                    type="radio"
                    {...register("color")}
                    value={color}
                    className="sr-only"
                  />
                  <div
                    className="w-8 h-8 rounded-full border-2 border-gray-200 hover:border-gray-400 transition-colors duration-200"
                    style={{ backgroundColor: color }}
                  />
                </label>
              ))}
            </div>
            <Input
              type="color"
              {...register("color")}
              className="h-10 w-full cursor-pointer"
            />
          </div>

          <div className="flex gap-3 pt-4">
            <Button
              type="button"
              variant="secondary"
              onClick={onClose}
              className="flex-1"
            >
              Cancelar
            </Button>
            <Button type="submit" disabled={isLoading} className="flex-1">
              {isLoading ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : isEditing ? (
                "Guardar"
              ) : (
                "Crear"
              )}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
